// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Hide from Chats" on a mail row in the chat lists: hide every mail
// from this sender, or every mail whose subject has the suggested phrase. The
// rows go at once and a notice offers Undo. Only the chats menu offers this;
// the Mail page's own rows do not.

import MailChatHide from '@app/features/konvo_mail/state/MailChatHide';
import {type MailChatHideRule, rulesOfferedFor} from '@app/features/konvo_mail/utils/MailChatHiding';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ArrowCounterClockwiseIcon, AtIcon, EyeSlashIcon, QuotesIcon} from '@app/features/ui/icons/Icons';
import type {MenuGroupType, MenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const HIDE_FROM_CHATS_DESCRIPTOR = msg({
	message: 'Hide from Chats',
	comment:
		'Mail row menu in Chats: opens the choices to stop showing mails like this one in the chat lists. They stay in Mail.',
});
export const HIDE_SENDER_DESCRIPTOR = msg({
	message: 'Mail from {address}',
	comment:
		'Choice under "Hide from Chats" on a mail row: hides every mail from this sender. {address} is an email address like notifications@github.com.',
});
export const HIDE_SUBJECT_DESCRIPTOR = msg({
	message: 'Subjects containing “{phrase}”',
	comment:
		'Choice under "Hide from Chats" on a mail row: hides every mail whose subject has this phrase. {phrase} is part of the subject, like PR run failed.',
});
export const HIDDEN_FROM_CHATS_DESCRIPTOR = msg({
	message: 'Hidden from Chats',
	comment: 'Notice after hiding mails from the chat lists; an Undo button follows. The rule is shown under it.',
});
export const UNDO_DESCRIPTOR = msg({message: 'Undo', comment: 'Button on the "Hidden from Chats" notice.'});
export const DISMISS_DESCRIPTOR = msg({
	message: 'Dismiss',
	comment: 'Accessible label of the close button on the "Hidden from Chats" notice.',
});

const RULE_SENDER_DESCRIPTOR = msg({
	message: 'From {address}',
	comment:
		'A rule in Settings > Mail > Hidden from Chats, also under the "Hidden from Chats" notice. {address} is an email address like notifications@github.com.',
});
const RULE_SUBJECT_DESCRIPTOR = msg({
	message: 'Subject contains “{phrase}”',
	comment:
		'A rule in Settings > Mail > Hidden from Chats, also under the "Hidden from Chats" notice. {phrase} is part of a mail subject, like PR run failed.',
});

const NOTICE_MS = 8000;

/** "From notifications@github.com" / "Subject contains “PR run failed”". */
export function ruleLabel(i18n: I18n, rule: MailChatHideRule): string {
	return rule.kind === 'sender'
		? i18n._(RULE_SENDER_DESCRIPTOR, {address: rule.value})
		: i18n._(RULE_SUBJECT_DESCRIPTOR, {phrase: rule.value});
}

/** Adds the rule, so its rows leave every chat list at once, and offers Undo. */
export function hideMailFromChats(i18n: I18n, rule: MailChatHideRule): void {
	if (!MailChatHide.add(rule)) return;
	ToastCommands.createToast({
		type: 'info',
		timeout: NOTICE_MS,
		leading: <EyeSlashIcon size={16} />,
		children: i18n._(HIDDEN_FROM_CHATS_DESCRIPTOR),
		detail: ruleLabel(i18n, rule),
		action: {
			label: i18n._(UNDO_DESCRIPTOR),
			icon: <ArrowCounterClockwiseIcon size={15} />,
			onClick: () => MailChatHide.remove(rule),
			'data-flx': 'konvo-mail.chat-hide.undo',
		},
		dismissLabel: i18n._(DISMISS_DESCRIPTOR),
	});
}

function ruleItem(i18n: I18n, rule: MailChatHideRule, close: () => void): MenuItemType {
	return {
		id: `hide_${rule.kind}`,
		label:
			rule.kind === 'sender'
				? i18n._(HIDE_SENDER_DESCRIPTOR, {address: rule.value})
				: i18n._(HIDE_SUBJECT_DESCRIPTOR, {phrase: rule.value}),
		icon: rule.kind === 'sender' ? <AtIcon size={18} /> : <QuotesIcon size={18} />,
		onClick: () => {
			close();
			hideMailFromChats(i18n, rule);
		},
	};
}

/**
 * The Hide from Chats choices for a mail row: a submenu on desktop, a titled
 * group of its own on the phone sheet. Empty when the mail offers no rule.
 */
export function mailChatHideGroups(
	i18n: I18n,
	message: {from_address: string | null; subject: string},
	close: () => void,
	phone: boolean,
): Array<MenuGroupType> {
	const items = rulesOfferedFor(message).map((rule) => ruleItem(i18n, rule, close));
	if (items.length === 0) return [];
	if (phone) {
		return [{title: i18n._(HIDE_FROM_CHATS_DESCRIPTOR), titleIcon: <EyeSlashIcon size={16} />, items}];
	}
	return [
		{
			items: [
				{id: 'hide_from_chats', label: i18n._(HIDE_FROM_CHATS_DESCRIPTOR), icon: <EyeSlashIcon size={18} />, items},
			],
		},
	];
}
